import { createContext, useContext, useEffect, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import { API_BASE } from "../config";
import { fetchWithAuth } from "../utils/fetchWithAuth";

const EMPTY_USER = {
  gid: null,
  email: null,
  roles: {},
  scope: null,
};

const UserContext = createContext({
  user: EMPTY_USER,
  roles: {},
  loading: false,
  error: "",
});

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Backend did not return valid JSON.");
  }
}

function getBackendErrorMessage(data, fallback) {
  return (
    data?.message ||
    data?.detail ||
    data?.error ||
    data?.errors?.[0]?.message ||
    fallback
  );
}

export function UserProvider({ children }) {
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(EMPTY_USER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isCancelled = false;

    // Fail closed until authenticated and /me resolves: keep roles empty.
    if (!isAuthenticated) {
      setUser(EMPTY_USER);
      setLoading(false);
      setError("");
      return;
    }

    const fetchMe = async () => {
      setLoading(true);
      setError("");

      try {
        const res = await fetchWithAuth(`${API_BASE}/ainstein/me`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        let raw = null;

        try {
          raw = await readJsonResponse(res);
        } catch (jsonError) {
          if (res.ok) {
            throw jsonError;
          }
        }

        if (!res.ok) {
          throw new Error(
            getBackendErrorMessage(
              raw,
              `Failed to load current user (status ${res.status}).`
            )
          );
        }

        if (!isCancelled) {
          setUser({
            gid: raw?.gid ?? null,
            email: raw?.email ?? null,
            roles: raw?.roles && typeof raw.roles === "object" ? raw.roles : {},
            scope: raw?.scope ?? null,
          });
          setError("");
        }
      } catch (err) {
        if (!isCancelled) {
          // Fail closed: clear roles so role-gated buttons stay hidden.
          setUser(EMPTY_USER);
          setError(err?.message || "Failed to load current user.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchMe();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated]);

  return (
    <UserContext.Provider value={{ user, roles: user.roles, loading, error }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
